import _forIn from 'lodash/forIn';
import Model from '$dependencies/Model';
import PhotoCollection from '$collections/PhotoCollection';
import Config from '../../Config';

const FIELDS = {
	uuid: { type: String, default: '', identifier: true },
	title: { type: String, default: '' },
	created: { type: String, default: '' },
	color: { type: Object, default: () => emptyTerm() },
	volume: { type: Number, default: 0 },
	material: { type: Object, default: () => emptyTerm() },
	binId: { type: String, default: '' },
	customBinId: { type: String, default: '' },
	status: { type: Boolean, default: true },
	ashtray: { type: Boolean, default: false },
	postalCode: { type: String, default: '' },
	city: { type: String, default: '' },
	street: { type: String, default: '' },
	remark: { type: String, default: '' },
	lastMeasurement: { type: String, default: '' },
	firstMeasurement: { type: String, default: '' },
	locationType: { type: Object, default: () => emptyTerm() },
	emptyingFrequency: { type: Object, default: () => emptyTerm() },
	emptyingFrequencyRemark: { type: String, default: '' },
	emptying_days: { type: Array, default: () => [] },
	damageAmount: { type: Object, default: () => emptyTerm() },
	damageType: { type: Object, default: () => emptyTerm() },
	damageRemark: { type: Object, default: () => emptyDamageRemark() },
	location: { type: Object, default: () => ({ lat: null, lon: null }) },
	type: { type: Object, default: () => emptyTerm() },
	fraction: { type: Object, default: () => emptyTerm() },
	requiredAction: { type: Object, default: () => emptyTerm() },
	images: { type: Object, default: () => new PhotoCollection(), referenceType: 'file--file' },
	analysis: { type: Object, default: () => ({}) },
	fillDegreeCount: { type: Object, default: () => ({}) },
	lidSize: { type: Number, default: 0 },
	dumpCount: { type: Number, default: 0 },
	additionalDumpCount: { type: Number, default: 0 },
	measurementCount: { type: Number, default: 0 },
	incidentCount: { type: Number, default: 0 },
	lastIncidentDate: { type: String, default: '' },
	remarkCount: { type: Number, default: 0 },
	lastRemark: { type: Object, default: () => ({}) },
	surface: { type: Object, default: () => emptyTerm() },
	allRemarks: { type: Array, default: () => [] },
	bins: { type: Array, default: () => [] },
	validityScore: { type: Number },
	deletePermission: { type: Boolean, default: null },
	qr: { type: String, default: '' },
	qrCodeUrl: { type: String, default: '' },
	qrCodeBlob: { type: String, default: '' },
};

export default class BinModel extends Model {
	constructor(args = {}) {
		args = Object.assign({}, args);
		args.fields = FIELDS;
		args.identifier = args.identifier || 'uuid';

		super(args);

		this.include = [];
	}

	fetch(data) {
		this.setConfig('url', `${Config.getUrl('c_bins')}/${this.uuid || this.id}`);
		return super.fetch(data);
	}

	post(data) {
		this.setConfig('url', Config.getUrl('c_bins'));
		return super.post(prepareBinPayload(data || this.toObject(true), 'post'));
	}

	patch(data, options = {}) {
		this.setConfig('url', `${Config.getUrl('c_bins')}/${this.uuid || this.id}`);
		const payload = prepareBinPayload(data || this.toObject(true), 'patch');
		const cleaned = cleanUpPatchFields(payload, options.fieldsToPatch);
		// Keep nested damage when any legacy damage field was marked dirty.
		if (cleaned.damage === undefined && payload.damage && options.fieldsToPatch) {
			const mapped = options.fieldsToPatch.map(mapPatchFieldName);
			if (mapped.includes('damage')) {
				cleaned.damage = payload.damage;
			}
		}
		return super.patch(cleaned);
	}

	delete() {
		this.setConfig('url', `${Config.getUrl('c_bins')}/${this.uuid || this.id}`);
		return super.delete();
	}

	set(model, options = {}) {
		if (
			model &&
			typeof model === 'object' &&
			!Array.isArray(model) &&
			!(model instanceof Model) &&
			model.id &&
			!model.uuid
		) {
			model = { ...model, uuid: model.id };
		}
		return super.set(model, options);
	}

	parse(data) {
		if (!data) return {};

		const payload = data.data ? { ...data.data } : { ...data };
		if (payload.id && !payload.uuid) {
			payload.uuid = payload.id;
		}
		normalizeBinPayload(payload);
		hydrateImages(this, payload);
		return super.parse(payload);
	}

	async setDeletePermission() {
		this.deletePermission = false;
		const response = await fetch(`${Config.getUrl('binDeletePermission')}/${this.id}`, {
			headers: this._config.headers,
		});
		const permission = await response.json();
		this.deletePermission = !!permission.allowed;
		return this.deletePermission;
	}

	checkforChangedFields(other) {
		const current = prepareBinPayload(this.toObject(true), 'patch');
		const next = prepareBinPayload((other && other.toObject ? other.toObject(true) : other) || {}, 'patch');
		return Object.keys(next).filter((key) => JSON.stringify(current[key]) !== JSON.stringify(next[key]));
	}

	get analyseData() {
		const analyseSets = [];

		_forIn(this.analysis, (value, key) => {
			if (key !== 'count') {
				analyseSets.push(createAnalyseSet(key, value, this.analysis.count));
			}
		});

		return analyseSets;
	}

	get fillDegreeData() {
		const modifiers = [0, 50, 100, 125];
		const analyseSet = createAnalyseSet('fillDegree', this.fillDegreeCount, this.measurementCount, Config.garbageBin.fillDegreeLabels);
		for (let i = 0; i < analyseSet.data.length; i++) {
			analyseSet.data[i].modifier = modifiers[i];
		}
		return analyseSet;
	}

	get id() {
		return this.uuid;
	}

	set id(val) {
		this.uuid = val;
	}

	get active() {
		return isBinActive(this.status);
	}

	set active(val) {
		this.status = isBinActive(val);
	}

	get custom_id() {
		return this.customBinId;
	}

	set custom_id(val) {
		this.customBinId = val;
	}

	get assigned_id() {
		return this.binId;
	}

	set assigned_id(val) {
		this.binId = val;
	}

	get bin_type() {
		return this.type;
	}

	set bin_type(val) {
		this.type = normalizeTerm(val);
	}

	get location_type() {
		return this.locationType;
	}

	set location_type(val) {
		this.locationType = normalizeTerm(val);
	}

	get lid_size() {
		return this.lidSize;
	}

	set lid_size(val) {
		this.lidSize = val;
	}

	get bin_ashtray() {
		return this.ashtray;
	}

	set bin_ashtray(val) {
		this.ashtray = val;
	}

	get emptying_frequency() {
		return this.emptyingFrequency;
	}

	set emptying_frequency(val) {
		this.emptyingFrequency = normalizeTerm(val);
	}

	get emptying_frequency_exception() {
		return this.emptyingFrequencyRemark;
	}

	set emptying_frequency_exception(val) {
		this.emptyingFrequencyRemark = val;
	}

	get last_measurement() {
		return this.lastMeasurement;
	}

	set last_measurement(val) {
		this.lastMeasurement = val;
	}

	get first_measurement() {
		return this.firstMeasurement;
	}

	set first_measurement(val) {
		this.firstMeasurement = val;
	}

	get post_code() {
		return this.postalCode;
	}

	set post_code(val) {
		this.postalCode = typeof val === 'object' ? val && (val.postalCode || val.title) : val;
	}

	get geolocation() {
		return this.location;
	}

	set geolocation(val) {
		this.location = val || { lat: null, lon: null };
	}

	get required_action() {
		return this.requiredAction;
	}

	set required_action(val) {
		this.requiredAction = normalizeTerm(val);
	}

	get bin_damage() {
		return this.damageAmount;
	}

	set bin_damage(val) {
		this.damageAmount = normalizeTerm(val);
	}

	get bin_damage_remark() {
		return this.damageRemark;
	}

	set bin_damage_remark(val) {
		this.damageRemark = val || emptyDamageRemark();
	}

	get measurement_count() {
		return this.measurementCount;
	}

	set measurement_count(val) {
		this.measurementCount = val;
	}

	get incident_count() {
		return this.incidentCount;
	}

	set incident_count(val) {
		this.incidentCount = val;
	}

	get last_incident() {
		return this.lastIncidentDate;
	}

	set last_incident(val) {
		this.lastIncidentDate = val;
	}

	get field_bin_type_parent_name() {
		const type = this.type || {};
		if (type.parent && typeof type.parent === 'object') {
			return type.parent.title || type.parent.name || '';
		}
		const fraction = this.fraction || {};
		const fractionName = fraction.title || fraction.name || '';
		const typeName = type.title || type.name || '';
		if (fractionName && fractionName !== typeName) return fractionName;
		return '';
	}

	get postalCodeValue() {
		if (this.postalCode && typeof this.postalCode === 'object') {
			return this.postalCode.postalCode || this.postalCode.title || '';
		}
		return this.postalCode || '';
	}

	get fullAddress() {
		let full = '';
		const postal = this.postalCodeValue;

		if (this.street) full += this.street;
		if (this.street && (postal || this.city)) full += ', ';
		if (postal) full += postal;
		if (this.city) full += ` ${this.city}`;

		return full;
	}

	get coordinates() {
		return { latitude: this.location.lat, longitude: this.location.lon };
	}

	get gmapCoordinates() {
		return { lat: this.location.lat, lng: this.location.lon };
	}

	get latitude() {
		return this.location && this.location.lat;
	}

	get longitude() {
		return this.location && this.location.lon;
	}

	set latitude(val) {
		this.location = { ...(this.location || {}), lat: val };
	}

	set longitude(val) {
		this.location = { ...(this.location || {}), lon: val };
	}
}

function emptyTerm() {
	return { uuid: '', id: '', title: '', name: '', label: '', value: '', parent: '' };
}

function emptyDamageRemark() {
	return {
		id: '',
		content: '',
		damageType: emptyTerm(),
	};
}

function normalizeTerm(value) {
	if (!value) return emptyTerm();
	if (typeof value === 'string') {
		return { uuid: value, id: value, title: '', name: '', label: '', value, parent: '' };
	}

	const uuid = value.uuid || value.id || '';
	const title = value.title || value.name || '';

	return {
		...value,
		uuid,
		id: uuid,
		title,
		name: title,
		label: value.label || title,
		value: uuid,
		parent: value.parent || '',
	};
}

function termId(term) {
	if (!term) return undefined;
	if (typeof term === 'string') return term || undefined;
	return term.uuid || term.id || undefined;
}

function isBinActive(value) {
	if (value === false || value === 0 || value === '0' || value === 'false' || value === 'inactive') return false;
	if (value && typeof value === 'object') {
		return isBinActive(value.status ?? value.active ?? value.value);
	}
	return true;
}

function toDateValue(value) {
	if (value === null || value === undefined || value === '') return value;
	if (value instanceof Date) return value;
	const asNumber = Number(value);
	if (!Number.isNaN(asNumber) && String(value).match(/^\d+$/)) {
		return new Date(asNumber > 1e12 ? asNumber : asNumber * 1000);
	}
	return value;
}

const WITHOUT_DAMAGE_ID = 'da9d800f-d884-4149-a4a8-4cd37eba8fe4';

function unwrapDamage(payload) {
	const damage = payload.damage;
	if (!damage || typeof damage !== 'object' || Array.isArray(damage)) return;

	if (!payload.damageAmount && (damage.amount || damage.uuid || damage.id)) {
		payload.damageAmount = damage.amount || damage;
	}

	const remark = damage.remark;
	if (remark && typeof remark === 'object') {
		if (!payload.damageType && (remark.type || remark.damageType)) {
			payload.damageType = remark.type || remark.damageType;
		}
		if (!payload.damageRemark) {
			payload.damageRemark = {
				content: remark.content || remark.other || '',
				damageType: remark.type || remark.damageType,
			};
		}
	}
}

function normalizeBinPayload(payload) {
	unwrapDamage(payload);

	if (payload.active !== undefined && (payload.status === undefined || payload.status === null || payload.status === '')) {
		payload.status = payload.active;
	}
	if (payload.status !== undefined) {
		payload.status = isBinActive(payload.status);
	}

	['type', 'color', 'material', 'surface', 'locationType', 'emptyingFrequency', 'requiredAction', 'damageAmount', 'damageType', 'fraction'].forEach((key) => {
		if (payload[key] !== undefined) {
			payload[key] = normalizeTerm(payload[key]);
		}
	});

	const remarkSource = payload.damageRemark;
	const remark = remarkSource && typeof remarkSource === 'object' ? remarkSource : {};
	const content = typeof remarkSource === 'string' ? remarkSource : (remark.content || remark.other || '');
	const remarkType = remark.damageType || remark.type || payload.damageType;

	payload.damageRemark = {
		id: termId(remark) || termId(remarkType) || '',
		content,
		damageType: normalizeTerm(remarkType),
	};
	if (termId(remarkType)) {
		payload.damageType = normalizeTerm(remarkType);
	}

	['created', 'lastMeasurement', 'firstMeasurement', 'lastIncidentDate'].forEach((key) => {
		if (payload[key] !== undefined) {
			payload[key] = toDateValue(payload[key]);
		}
	});

	if (payload.location && (payload.location.lat || payload.location.lon)) {
		payload.location = {
			lat: payload.location.lat,
			lon: payload.location.lon,
		};
	}

	if (payload.postalCode && typeof payload.postalCode === 'object') {
		payload.postalCode = payload.postalCode.postalCode || payload.postalCode.title || '';
	}

	// Pro API returns qrCodeUrl; keep qr as the display URL for PhotosFormField
	const qrValue = payload.qrCodeUrl
		|| payload.qr
		|| payload.qrCode
		|| payload.qrCodeBlob
		|| payload.qr_blob
		|| payload.field_bin_qr
		|| '';
	if (qrValue) {
		payload.qr = qrValue;
		payload.qrCodeUrl = payload.qrCodeUrl || qrValue;
		if (String(qrValue).indexOf('data:') === 0) {
			payload.qrCodeBlob = qrValue;
		}
	}
}

function hydrateImages(model, payload) {
	const images = Array.isArray(payload.images) ? payload.images : [];
	delete payload.images;

	if (!model.images) return;

	model.images.clear();
	images.forEach((image) => {
		model.images.set({
			id: image.uuid || image.id || '',
			uuid: image.uuid || image.id || '',
			url: image.url || '',
			original: image.original || image.originalUrl || image.url || '',
			originalUrl: image.originalUrl || image.original_url || image.original || image.url || '',
		});
	});
}

function prepareBinPayload(data, method) {
	const payload = {};
	const location = data.location || data.geolocation || {};

	payload.ashtray = data.ashtray;
	payload.city = data.city;
	payload.color = termId(data.color);
	payload.customBinId = data.customBinId;
	payload.emptyingFrequency = termId(data.emptyingFrequency);
	payload.lidSize = data.lidSize === '' || data.lidSize === undefined ? undefined : Number(data.lidSize);
	payload.locationType = termId(data.locationType);
	payload.material = termId(data.material);
	payload.postalCode = data.postalCode && typeof data.postalCode === 'object'
		? data.postalCode.postalCode || data.postalCode.title
		: data.postalCode;
	payload.street = data.street;
	payload.surface = termId(data.surface);
	payload.type = termId(data.fraction) || termId(data.type);
	payload.volume = data.volume === '' || data.volume === undefined ? undefined : Number(data.volume);
	const remarkValue = typeof data.remark === 'object' && data.remark
		? (data.remark.value || data.remark.content || '')
		: data.remark;
	if (remarkValue !== undefined && remarkValue !== null && String(remarkValue).trim() !== '') {
		payload.remark = remarkValue;
	}
	payload.emptyingFrequencyRemark = data.emptyingFrequencyRemark;

	if (location.lat != null && location.lon != null) {
		payload.location = {
			lat: Number(location.lat),
			lon: Number(location.lon),
		};
	}

	if (method === 'patch') {
		payload.status = isBinActive(data.status);

		const damageAmountId = termId(data.damageAmount);
		const damageTypeId = termId(data.damageRemark && data.damageRemark.damageType) || termId(data.damageType);
		const content = data.damageRemark && data.damageRemark.content;

		if (damageAmountId) {
			const damage = { amount: damageAmountId };

			if (damageAmountId !== WITHOUT_DAMAGE_ID) {
				// API requires remark.type whenever amount is not "without damage".
				if (damageTypeId) {
					damage.remark = {
						content: content || '',
						type: damageTypeId,
					};
				}
			}

			payload.damage = damage;
		}
	}

	Object.keys(payload).forEach((key) => {
		if (payload[key] === undefined || payload[key] === '') {
			delete payload[key];
		}
	});

	return payload;
}

function cleanUpPatchFields(data, fieldsToPatch) {
	if (!fieldsToPatch) return data;
	if (data && fieldsToPatch) {
		const mapped = fieldsToPatch.map(mapPatchFieldName);
		for (const field in data) {
			if (!mapped.includes(field)) {
				delete data[field];
			}
		}
	}
	return data;
}

function mapPatchFieldName(field) {
	const dictionary = {
		custom_id: 'customBinId',
		bin_type: 'type',
		location_type: 'locationType',
		lid_size: 'lidSize',
		bin_ashtray: 'ashtray',
		emptying_frequency: 'emptyingFrequency',
		emptying_frequency_exception: 'emptyingFrequencyRemark',
		post_code: 'postalCode',
		geolocation: 'location',
		bin_damage: 'damage',
		bin_damage_remark: 'damage',
		damageAmount: 'damage',
		damageType: 'damage',
		damageRemark: 'damage',
		active: 'status',
	};
	return dictionary[field] || field;
}

function createAnalyseSet(key, value, total, dictionary = {}) {
	const set = {
		title: key,
		data: [],
	};

	_forIn(value, (count, label) => {
		set.data.push({
			label: dictionary[label] ? dictionary[label] : label,
			percentage: count,
		});
	});

	set.data.map((ds) => {
		ds.percentage = ds.percentage > 0 ? Math.round((100 / total) * ds.percentage) : 0;
	});

	return set;
}
